import React, { useEffect } from 'react';

import { concat, isEmpty } from 'lodash';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { connect, useDispatch } from 'react-redux';

import { useUnitHistoryItemsQuery } from '@modules/advancement/pendingItems/hooks';
import fileFromString from '@modules/utils/fileFromString';
import toggleArrayItem from '@modules/utils/toggleArrayItem';
import { Alert, SearchInput } from '@shared/components';
import { sorterPropTypes } from '@shared/constants';
import { goToAdultProfile } from '@shared/duck/actions';
import { goToYouthProfile } from '@shared/duck/actionsTyped';
import { pendingApprovableAdvancementsSel } from '@shared/duck/selectors/advancementHistory.selectors';
import {
  isMobileSel,
  offlineSel,
} from '@shared/duck/selectors/general.selectors';
import { intl } from '@shared/localization';

import { cancelFileRequest, cancellingSel } from '../../../advancementUpload';
import { activityItemsTypes } from '../../../common';
import {
  advancementHistoryResponse,
  expandedKeysSel,
  filteredItemsSectionsSel,
  searchFilterSel,
  selectedKeysSel,
  setExpandedKeys,
  setSearchFilter,
  setSelectedRowKeys,
  setSorter,
  sorterSel,
  updatedDateFilterSel,
} from '../../duck';
import AdvancementHistoryList from './AdvancementHistoryList';
import AdvancementHistoryTable from './AdvancementHistoryTable';

class AdvancementHistory extends React.PureComponent {
  state = {
    searchPinned: false,
  };

  handleSearch = search => {
    this.props.onSetSearch(search);
  };

  handleSorterChange = sorter => {
    this.props.onSetSorter(sorter);
  };

  handleExpandedKeyChange = key => {
    const { expandedKeys } = this.props;
    const newKeys = toggleArrayItem(expandedKeys, key);

    this.props.onSetExpandedKeys(newKeys);
  };

  handleSearchPinChange = searchPinned => this.setState({ searchPinned });

  handleCancelFile = id => {
    const { items } = this.props;
    const { name } = items[activityItemsTypes.HISTORY].find(
      item => item.id === id,
    );
    const successMsg = (
      <FormattedMessage
        id="advancement.AdvancementHistory.cancelFileSuccessMsg"
        values={{ name }}
      />
    );
    this.props.onCancelFile({ id, successMsg });
  };

  handleDownloadFileErrorLog = ({ errors, date }) => {
    if (errors) {
      fileFromString(errors, `import-${date}-log.txt`);
    }
  };

  renderSearch() {
    const { search } = this.props;
    return (
      <SearchInput
        key="search"
        id="qa_inputSearchActivity"
        size="large"
        mobileSticky
        placeholder={this.getSearchPlaceholder()}
        value={search}
        onChange={this.handleSearch}
        onPinChange={this.handleSearchPinChange}
      />
    );
  }

  getSearchPlaceholder = () => {
    const { isMobile, updatedDateFilter } = this.props;
    const messageId = isMobile
      ? 'advancement.AdvancementHistory.search.mobile'
      : `advancement.AdvancementHistory.search.${updatedDateFilter}`;
    return intl.formatMessage({
      id: messageId,
    });
  };

  handleGoToYouthProfile =
    ({ userId }) =>
    event => {
      if (!this.props.isOffline && userId) {
        event.stopPropagation();
        this.props.onGoToYouthProfile(userId);
      }
    };

  handleGoToAdultProfile =
    ({ userId }) =>
    event => {
      if (!this.props.isOffline && userId) {
        event.stopPropagation();
        this.props.onGoToAdultProfile(userId);
      }
    };

  renderError() {
    const { loading, isError, items } = this.props;

    if (loading || !isError || !isEmpty(items[activityItemsTypes.HISTORY])) {
      return null;
    }

    return (
      <Alert
        message={<FormattedMessage id="shared.errors.generic" />}
        showIcon
        type="error"
      />
    );
  }

  renderItems() {
    const {
      loading,
      items,
      expandedKeys,
      sorter,
      isMobile,
      cancellingFiles,
      pendingApprovableItems,
      selectedRowKeys,
      onSetSelectedRowsKeys,
      isOffline,
    } = this.props;

    const { searchPinned } = this.state;

    return isMobile ? (
      <AdvancementHistoryList
        key="list"
        items={items}
        pendingApprovableItems={pendingApprovableItems}
        loading={loading}
        expandedKeys={expandedKeys}
        searchPinned={searchPinned}
        sorter={sorter}
        isOffline={isOffline}
        cancellingFiles={cancellingFiles}
        selectedRowKeys={selectedRowKeys}
        onSetSelectedRowsKeys={onSetSelectedRowsKeys}
        onSorterChange={this.handleSorterChange}
        onExpandedKeyChange={this.handleExpandedKeyChange}
        onCancelFile={this.handleCancelFile}
        onGoToYouthProfile={this.handleGoToYouthProfile}
        onGoToAdultProfile={this.handleGoToAdultProfile}
        onDownloadFileErrorLog={this.handleDownloadFileErrorLog}
      />
    ) : (
      <AdvancementHistoryTable
        key="table"
        items={items}
        pendingApprovableItems={pendingApprovableItems}
        selectedRowKeys={selectedRowKeys}
        onSetSelectedRowsKeys={onSetSelectedRowsKeys}
        loading={loading}
        isOffline={isOffline}
        expandedKeys={expandedKeys}
        sorter={sorter}
        cancellingFiles={cancellingFiles}
        onSorterChange={this.handleSorterChange}
        onExpandedKeyChange={this.handleExpandedKeyChange}
        onCancelFile={this.handleCancelFile}
        onGoToYouthProfile={this.handleGoToYouthProfile}
        onGoToAdultProfile={this.handleGoToAdultProfile}
        onDownloadFileErrorLog={this.handleDownloadFileErrorLog}
      />
    );
  }

  render() {
    return (
      <>
        {this.renderSearch()}
        {this.renderError()}
        {this.renderItems()}
      </>
    );
  }
}

AdvancementHistory.propTypes = {
  items: PropTypes.object.isRequired,
  loading: PropTypes.bool.isRequired,
  expandedKeys: PropTypes.array.isRequired,
  isMobile: PropTypes.bool.isRequired,
  isError: PropTypes.bool.isRequired,
  search: PropTypes.string,
  updatedDateFilter: PropTypes.string.isRequired,
  sorter: sorterPropTypes,
  cancellingFiles: PropTypes.object.isRequired,
  pendingApprovableItems: PropTypes.array.isRequired,
  selectedRowKeys: PropTypes.array.isRequired,
  isOffline: PropTypes.bool.isRequired,
  onSetSorter: PropTypes.func.isRequired,
  onCancelFile: PropTypes.func.isRequired,
  onSetSearch: PropTypes.func.isRequired,
  onGoToYouthProfile: PropTypes.func.isRequired,
  onGoToAdultProfile: PropTypes.func.isRequired,
  onSetExpandedKeys: PropTypes.func.isRequired,
  onSetSelectedRowsKeys: PropTypes.func.isRequired,
};

const mapState = state => ({
  isMobile: isMobileSel(state),
  expandedKeys: expandedKeysSel(state),
  search: searchFilterSel(state),
  updatedDateFilter: updatedDateFilterSel(state),
  sorter: sorterSel(state),
  cancellingFiles: cancellingSel(state),
  pendingApprovableItems: pendingApprovableAdvancementsSel(state),
  selectedRowKeys: selectedKeysSel(state),
  items: filteredItemsSectionsSel(state),
  isOffline: offlineSel(state),
});

const mapDispatch = dispatch => ({
  onSetExpandedKeys: keys => dispatch(setExpandedKeys(keys)),
  onSetSearch: search => dispatch(setSearchFilter(search)),
  onSetSorter: sorter => dispatch(setSorter(sorter)),
  onCancelFile: params => dispatch(cancelFileRequest(params)),
  onSetSelectedRowsKeys: ids => dispatch(setSelectedRowKeys(ids)),
  onGoToYouthProfile: userId => dispatch(goToYouthProfile(userId)),
  onGoToAdultProfile: userId => dispatch(goToAdultProfile(userId)),
});

const AdvancementHistoryContainer = props => {
  const dispatch = useDispatch();
  const {
    activities,
    advancementsResponse: { data: results },
    isLoading,
    isError,
    unitImports,
    campImports,
  } = useUnitHistoryItemsQuery();

  useEffect(() => {
    dispatch(
      advancementHistoryResponse(
        concat(results, activities, unitImports, campImports),
      ),
    );
  }, [dispatch, results, activities, unitImports, campImports]);

  return (
    <AdvancementHistory {...props} loading={isLoading} isError={isError} />
  );
};

export default connect(mapState, mapDispatch)(AdvancementHistoryContainer);
